// Layout store: the profile's layout document in memory, edits through
// `update`, autosave to the Worker with the revision check, activity log.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ApiError, me, saveLayout, type Profile } from "../api";
import { emptyLayout, type ActivityEntry, type Layout } from "./types";

export type SaveStatus = "idle" | "dirty" | "saving" | "saved" | "conflict" | "error";

interface StoreValue {
  profile: Profile;
  layout: Layout;
  revision: number;
  saveStatus: SaveStatus;
  saveError: string;
  update: (fn: (layout: Layout) => Layout, opts?: { label?: string; noUndo?: boolean }) => void;
  undo: () => void;
  canUndo: boolean;
  undoLabel: string;
  logActivity: (entry: { text: string; meta?: string; tone?: string; time?: string }) => void;
  reload: () => Promise<void>;
  saveNow: () => Promise<void>;
}

const StoreContext = createContext<StoreValue | null>(null);
const AUTOSAVE_MS = 1500;

export function LayoutProvider({ profile, initial, initialRevision, children }: { profile: Profile; initial: Layout | null; initialRevision: number; children: ReactNode }) {
  const [layout, setLayout] = useState<Layout>(() => ({ ...emptyLayout(), ...(initial || {}) }));
  const [revision, setRevision] = useState(initialRevision);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [saveError, setSaveError] = useState("");
  const dirty = useRef(false);
  const saving = useRef(false);   // one save in flight at a time: a second would carry a stale revision and look like a conflict
  const latest = useRef(layout);
  latest.current = layout;
  const revisionRef = useRef(revision);
  revisionRef.current = revision;
  // UNDO: a bounded stack of layout snapshots taken just BEFORE a change, so
  // "un-delete" falls out for free — the deleted thing is still in the snapshot.
  // Logging and the undo restore itself are excluded, so the stack holds
  // meaningful actions, not log lines.
  const UNDO_MAX = 50;
  const UNDO_COALESCE_MS = 600;
  const undoStack = useRef<{ layout: Layout; label: string; at: number; labelled: boolean }[]>([]);
  const [undoView, setUndoView] = useState<{ depth: number; label: string }>({ depth: 0, label: "" });

  const saveNow = useCallback(async () => {
    if (!dirty.current || saving.current) return;
    dirty.current = false;
    saving.current = true;
    setSaveStatus("saving");
    try {
      const result = await saveLayout(latest.current, revisionRef.current);
      setRevision(result.revision);
      // An edit made while this save was in flight set dirty again. Calling that "saved" left the page holding
      // unsaved work under a "Saved" label with nothing scheduled to write it (found 2026-10-08).
      setSaveStatus(dirty.current ? "dirty" : "saved");
      setSaveError("");
    } catch (error) {
      dirty.current = true;
      if (error instanceof ApiError && error.status === 409) { setSaveStatus("conflict"); setSaveError("This profile was saved from another tab or device. Reload to continue."); return; }
      setSaveStatus("error");
      setSaveError(error instanceof Error ? error.message : "Save failed");
    } finally {
      saving.current = false;
    }
  }, []);

  useEffect(() => {
    if (saveStatus !== "dirty") return;
    const timer = window.setTimeout(() => { void saveNow(); }, AUTOSAVE_MS);
    return () => window.clearTimeout(timer);
  }, [layout, saveStatus, saveNow]);

  // Leaving with unsaved edits: warn, since a bearer-authenticated save cannot ride a beacon.
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => { if (dirty.current) { event.preventDefault(); event.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, []);

  // Compute the next document synchronously from the latest ref so successive
  // updates in one tick chain, and so the dirty flag never diverges from what
  // React ends up rendering (an updater with side effects could be discarded).
  const update = useCallback((fn: (layout: Layout) => Layout, opts?: { label?: string; noUndo?: boolean }) => {
    const prev = latest.current;
    const next = fn(prev);
    if (next === prev) return;
    if (!opts?.noUndo) {
      const stack = undoStack.current;
      const top = stack[stack.length - 1];
      // Coalesce a burst of unlabeled edits (typing, a dragged slider) into the
      // one step they feel like; a labeled action (a delete, a link) always
      // opens its own step so its name survives for the button.
      const coalesce = Boolean(top && !opts?.label && !top.labelled && Date.now() - top.at < UNDO_COALESCE_MS);
      if (coalesce) top.at = Date.now();
      else undoStack.current = [...stack, { layout: prev, label: opts?.label || "the last change", at: Date.now(), labelled: Boolean(opts?.label) }].slice(-UNDO_MAX);
      const s = undoStack.current;
      setUndoView({ depth: s.length, label: s.length ? s[s.length - 1].label : "" });
    }
    latest.current = next;
    dirty.current = true;
    setLayout(next);
    setSaveStatus("dirty");
  }, []);

  const undo = useCallback(() => {
    const stack = undoStack.current;
    if (!stack.length) return;
    const entry = stack[stack.length - 1];
    undoStack.current = stack.slice(0, -1);
    const s = undoStack.current;
    setUndoView({ depth: s.length, label: s.length ? s[s.length - 1].label : "" });
    // Restore the snapshot and note it — the restore bypasses `update`, so it
    // never becomes an undo step of its own.
    const record: ActivityEntry = { text: `Undid: ${entry.label}`, tone: "muted", time: new Date().toISOString() };
    const restored: Layout = { ...entry.layout, activity: [record, ...(entry.layout.activity || [])].slice(0, 200) };
    latest.current = restored;
    dirty.current = true;
    setLayout(restored);
    setSaveStatus("dirty");
  }, []);

  const logActivity = useCallback((entry: { text: string; meta?: string; tone?: string; time?: string }) => {
    const record: ActivityEntry = { ...entry, time: entry.time || new Date().toISOString() };
    update((l) => ({ ...l, activity: [record, ...(l.activity || [])].slice(0, 200) }), { noUndo: true });
  }, [update]);

  const reload = useCallback(async () => {
    const fresh = await me();
    // A null (empty 200) or malformed /me must NOT wipe the live layout to empty — bail and
    // keep what's on screen rather than resetting to emptyLayout with an undefined revision.
    if (!fresh || !fresh.profile) return;
    setLayout({ ...emptyLayout(), ...(fresh.layout || {}) });
    setRevision(fresh.revision);
    dirty.current = false;
    setSaveStatus("idle");
    setSaveError("");
  }, []);

  const value = useMemo<StoreValue>(() => ({ profile, layout, revision, saveStatus, saveError, update, undo, canUndo: undoView.depth > 0, undoLabel: undoView.label, logActivity, reload, saveNow }), [profile, layout, revision, saveStatus, saveError, update, undo, undoView, logActivity, reload, saveNow]);
  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore() {
  const value = useContext(StoreContext);
  if (!value) throw new Error("useStore must be used inside LayoutProvider");
  return value;
}
